import React, { Component } from 'react';
import noop from 'lodash/noop';
import xorBy from 'lodash/xorBy';
import {
	type SelectOption,
	type PickerProps as BaseProps,
	Picker,
} from '@atlassian/jira-common-components-picker';
import { injectIntlV2 as injectIntl, type IntlShapeV2 as IntlShape } from '@atlassian/jira-intl';
import { requestComponentOptions, requestComponentObjects } from '../services';
import type { SingleComponent } from '../types';
import messages from './messages';

const optionFromComponent = (component: SingleComponent): SelectOption => ({
	value: component.id,
	label: component.name,
});

const componentFromOption = (option: SelectOption): SingleComponent => ({
	id: option.value,
	name: option.label,
});

const hasSameComponentIds = (
	newComponents?: SingleComponent[],
	oldComponents?: SingleComponent[],
): boolean => {
	if (newComponents === undefined || oldComponents === undefined) {
		return newComponents === oldComponents;
	}
	return xorBy(newComponents, oldComponents, 'id').length === 0;
};

export type Props = {
	isRequired: boolean;
	isDisabled: boolean;
	isMulti: boolean;
	isClearableWithCrossIcon: boolean;
	label: string;
	placeholder: string;
	ignore: string[];
	baseUrl: string; // TODO FD-29389 Clean up this, it's just for backwards compatibility,
	projectKeyOrId: string;
	// If selectedComponents are specified with an empty string `name` value,
	// then those components' names will be individually loaded before the components
	// displayed.
	// This prevents the display of ugly empty picker items.
	// While this fetching is occurring, any correctly populated items will still be visible.
	// It is assumed that no valid component has an empty name, and therefore
	// an empty string `name` value signifies incomplete data.
	selectedComponents: SingleComponent[] | undefined;
	onSelected: (arg1: SingleComponent[] | undefined) => void;
	intl: IntlShape;
};

type State = {
	selectedComponents: SingleComponent[] | undefined;
	prevSelectedComponents: SingleComponent[] | undefined;
};

type PickerProps = BaseProps<SelectOption>;

// eslint-disable-next-line jira/react/no-class-components
export class ComponentPicker extends Component<Props, State> {
	static defaultProps = {
		isRequired: false,
		isDisabled: false,
		isMulti: false,
		isClearableWithCrossIcon: false,
		selectedComponents: undefined,
		label: '',
		placeholder: '',
		ignore: [],
		onSelected: noop,
	};

	static getDerivedStateFromProps(props: Props, state: State) {
		if (!hasSameComponentIds(props.selectedComponents, state.prevSelectedComponents)) {
			return {
				...state,
				selectedComponents: props.selectedComponents,
				prevSelectedComponentIds: props.selectedComponents,
			};
		}

		return null;
	}

	state = {
		selectedComponents: this.props.selectedComponents,
		prevSelectedComponents: this.props.selectedComponents,
	};

	componentDidMount() {
		const { selectedComponents } = this.state;

		if (selectedComponents && selectedComponents.some((x) => !x.name)) {
			this.fetchAndUpdateComponents(selectedComponents);
		}
	}

	componentDidUpdate() {
		const { selectedComponents } = this.state;

		if (selectedComponents && selectedComponents.some((x) => !x.name)) {
			this.fetchAndUpdateComponents(selectedComponents);
		}
	}

	onMultiSelected = (selectedOptions?: ReadonlyArray<SelectOption>) => {
		if (selectedOptions) {
			this.setState({ selectedComponents: selectedOptions.map(componentFromOption) });
			this.props.onSelected(selectedOptions.map(componentFromOption));
		} else {
			this.setState({ selectedComponents: undefined });
			this.props.onSelected(undefined);
		}
	};

	onSelected = (selectedOption?: SelectOption) => {
		this.onMultiSelected(selectedOption && [selectedOption]);
	};

	fetchAndUpdateComponents(selectedComponents: SingleComponent[]) {
		const fillInSelectedComponents = (filledInComponents: SingleComponent[]) => {
			// Update the state only in case the component list wasn't changed
			if (hasSameComponentIds(this.state.selectedComponents, selectedComponents)) {
				this.setState({ selectedComponents: filledInComponents });
				this.props.onSelected(filledInComponents);
			}
		};

		requestComponentObjects(selectedComponents.map((x) => x.id))
			.then(fillInSelectedComponents)
			.catch(() => fillInSelectedComponents(selectedComponents.filter((x) => x.name)));
	}

	renderOptions = (filter: string) =>
		// @ts-expect-error - TS2349 - This expression is not callable.
		requestComponentOptions(
			this.props.projectKeyOrId,
			this.props.isMulti
				? this.props.ignore.concat(
						this.state.selectedComponents?.map((x: SingleComponent) => x.id) || [],
					)
				: this.props.ignore,
			filter,
		);

	render() {
		const { label, placeholder, isRequired, isDisabled, isClearableWithCrossIcon, isMulti, intl } =
			this.props;
		const selectedComponents = this.state.selectedComponents?.filter(
			(x: SingleComponent) => x.name,
		);

		const pickerProps: PickerProps = {
			label,
			placeholder,
			isRequired,
			isDisabled,
			isClearableWithCrossIcon,
			errorMessage: intl.formatMessage(messages.error),
			options: this.renderOptions,
			...(isMulti
				? {
						value: selectedComponents?.map(optionFromComponent),
						isMulti: true,
						onMultiSelected: this.onMultiSelected,
					}
				: {
						value:
							selectedComponents &&
							selectedComponents[0] &&
							optionFromComponent(selectedComponents[0]),
						isMulti: false,
						onSelected: this.onSelected,
					}),
		};

		return <Picker intl={intl} {...pickerProps} />;
	}
}

// @ts-expect-error - TS2345 - Argument of type 'typeof ComponentPicker' is not assignable to parameter of type 'ComponentType<Record<any, any>>'.
export default injectIntl(ComponentPicker);
